/** Lucide feather path data (lucide.dev/icons/feather) */
export const FEATHER_PATHS = {
  nib: 'M14.086 18.412A2 2 0 0112.67 19H5v-7.672a2 2 0 01.586-1.414L11.75 3.75a6 6 0 118.49 8.49z',
  shaft: 'M16 8 2 22',
  barb: 'M17.488 15H9',
};

export const FEATHER_LOADER_VARIANTS = [
  'idle',
  'connect',
  'think',
  'write',
  'search',
  'tools',
  'draft',
  'prepare',
  'recover',
];

/** Lucide feather default orientation has the nib left/up; flip so the tip points right. */
export const FEATHER_TIP_RIGHT = 'translate(20px, 0) scaleX(-1)';

const THINK_CYCLE_MS = 5000;
const WRITE_CYCLE_MS = 3200;
const DRAFT_CYCLE_MS = 4200;
const VARIANT_SETTLE_MS = 120;
/** Ease the live sway back to rest when streaming stops — no snap to origin. */
const RETURN_HOME_MS = 420;

const REST_POSE = { x: 0, y: 0, rotate: 0 };

const THINK_AVATAR_OPTS = {
  ampX: 3,
  ampY: 2,
  baseRot: 0,
  ampRot: 6,
  freqX: 2,
  freqRot: 3,
};

/**
 * Falling-leaf sway — parametric sinusoids (Lissajous-style, seamless loop).
 *   x(θ) = Ax·sin(fx·θ), y(θ) = Ay·sin(θ), rot(θ) = θ0 + Ar·sin(fr·θ)
 */
export function leafSwayPose(elapsedMs, cycleMs, opts = {}) {
  const {
    ampX = 6,
    ampY = 4,
    baseRot = 45,
    ampRot = 12,
    freqX = 2,
    freqRot = 3,
  } = opts;
  const theta = ((elapsedMs % cycleMs) / cycleMs) * Math.PI * 2;
  return {
    x: ampX * Math.sin(freqX * theta),
    y: ampY * Math.sin(theta),
    rotate: baseRot + ampRot * Math.sin(freqRot * theta),
  };
}

function writeTipPose(elapsedMs, cycleMs) {
  const { x, y, rotate } = leafSwayPose(elapsedMs, cycleMs, {
    ampX: 1.5,
    ampY: 0.6,
    baseRot: 8,
    ampRot: 1.5,
    freqX: 3,
    freqRot: 2,
  });
  return { x, y: y + 1, rotate };
}

function draftTipPose(elapsedMs, cycleMs) {
  const { x, y, rotate } = leafSwayPose(elapsedMs, cycleMs, {
    ampX: 2.5,
    ampY: 0.8,
    baseRot: 8,
    ampRot: 2,
    freqX: 2,
    freqRot: 1.5,
  });
  return { x, y: y + 1, rotate };
}

function getPose(variant, elapsedMs) {
  if (variant === 'think') {
    return leafSwayPose(elapsedMs, THINK_CYCLE_MS, THINK_AVATAR_OPTS);
  }
  if (variant === 'write') {
    return writeTipPose(elapsedMs, WRITE_CYCLE_MS);
  }
  if (variant === 'draft') {
    return draftTipPose(elapsedMs, DRAFT_CYCLE_MS);
  }
  return { ...REST_POSE };
}

function poseToCss({ x, y, rotate }) {
  if (x === 0 && y === 0 && rotate === 0) return '';
  return `rotate(${rotate}deg) translate(${x}px, ${y}px)`;
}

function scalePose({ x, y, rotate }, scale) {
  return { x: x * scale, y: y * scale, rotate: rotate * scale };
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function isNearRest({ x, y, rotate }) {
  return Math.abs(x) < 0.04 && Math.abs(y) < 0.04 && Math.abs(rotate) < 0.04;
}

/** Shared upright rest pose — all variants settle here between transitions. */
export function getFeatherRestTransform() {
  return poseToCss(REST_POSE);
}

const JS_DRIVEN_VARIANTS = new Set(['think', 'write', 'draft']);
const CSS_DRIVEN_VARIANTS = new Set([
  'connect',
  'search',
  'tools',
  'prepare',
  'recover',
]);

export function resolveCopilotFeatherVariant({ loadingPhase, assistantType }) {
  if (loadingPhase === 'authenticating' || loadingPhase === 'connecting') {
    return 'connect';
  }
  if (loadingPhase === 'preparing') return 'prepare';
  if (loadingPhase === 'starting') return 'think';
  if (assistantType === 'seo') return 'search';
  return 'think';
}

export function resolveMessageFeatherVariant({
  isStreaming,
  isThinkingInProgress,
  hasRunningToolCalls,
  hasResponseContent,
}) {
  if (!isStreaming) return 'idle';
  if (hasRunningToolCalls) return 'tools';
  if (isThinkingInProgress) return 'think';
  return 'think';
}

export function useFeatherLoaderMotion(featherRef, getVariant, getAnimated) {
  let rafId = null;
  let startedAt = 0;
  let settleUntil = 0;
  let runningVariant = 'idle';
  let motionVariant = 'idle';
  let homeStartedAt = 0;

  function isAnimated() {
    if (typeof getAnimated === 'function' && !getAnimated()) return false;
    const variant = getVariant();
    return variant !== 'idle' && JS_DRIVEN_VARIANTS.has(variant);
  }

  function applyPose(el, pose) {
    if (!el) return;
    el.style.transform = poseToCss(pose);
  }

  function applyRest() {
    homeStartedAt = 0;
    motionVariant = 'idle';
    applyPose(featherRef.value, { ...REST_POSE });
  }

  function restartCssAnimation(el) {
    if (!el) return;
    el.style.animation = 'none';
    // Force reflow so the next animation run starts at 0%.
    void el.offsetHeight;
    el.style.animation = '';
  }

  function tick(now) {
    const el = featherRef.value;
    const variant = getVariant();

    if (!el) {
      rafId = requestAnimationFrame(tick);
      return;
    }

    if (now < settleUntil) {
      applyPose(el, { ...REST_POSE });
      rafId = requestAnimationFrame(tick);
      return;
    }

    if (variant !== runningVariant) {
      const previousVariant = runningVariant;
      runningVariant = variant;
      const goingIdle = !isAnimated();
      if (!goingIdle) startedAt = now;
      const bothJsDriven =
        JS_DRIVEN_VARIANTS.has(previousVariant) &&
        JS_DRIVEN_VARIANTS.has(variant);
      if (!bothJsDriven && !goingIdle) {
        applyRest();
        restartCssAnimation(el);
      }
    }

    if (isAnimated()) {
      motionVariant = variant;
      homeStartedAt = 0;
      applyPose(el, getPose(variant, now - startedAt));
    } else if (
      JS_DRIVEN_VARIANTS.has(motionVariant) &&
      !CSS_DRIVEN_VARIANTS.has(variant)
    ) {
      if (!homeStartedAt) homeStartedAt = now;
      const t = Math.min(1, (now - homeStartedAt) / RETURN_HOME_MS);
      const live = getPose(motionVariant, now - startedAt);
      const pose =
        t >= 1 ? { ...REST_POSE } : scalePose(live, 1 - easeOutCubic(t));
      applyPose(el, isNearRest(pose) ? { ...REST_POSE } : pose);
      if (t >= 1) {
        homeStartedAt = 0;
        motionVariant = 'idle';
      }
    } else {
      applyPose(el, { ...REST_POSE });
    }

    rafId = requestAnimationFrame(tick);
  }

  function start() {
    startedAt = performance.now();
    runningVariant = getVariant();
    settleUntil = 0;
    homeStartedAt = 0;
    motionVariant = runningVariant;
    if (rafId) cancelAnimationFrame(rafId);
    applyRest();
    rafId = requestAnimationFrame(tick);
  }

  function stop() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    applyRest();
  }

  function resetCycle() {
    startedAt = performance.now();
    settleUntil = performance.now() + VARIANT_SETTLE_MS;
    runningVariant = 'idle';
    applyRest();
    restartCssAnimation(featherRef.value);
  }

  return { start, stop, resetCycle, applyRest, restartCssAnimation };
}
